/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { LocationTargetErrorType } from './location-targeting-error-type';

const errorMessageMap = {
  [LocationTargetErrorType.UNSUPPORTED_LOCATION]: {
    text: _TL_('Only {{supportedCountries}} locations are supported.'),
    shouldDisplayLink: false,
  },
  [LocationTargetErrorType.REDUNDANT_LOCATION]: {
    text: _TL_('This location is a duplicate target.'),
    shouldDisplayLink: true,
  },
  [LocationTargetErrorType.FACEBOOK_AND_GOOGLE_NOT_SUPPORTED]: {
    text: _TL_('Facebook and Google don\'t support this location.'),
    shouldDisplayLink: true,
  },
  [LocationTargetErrorType.FACEBOOK_NOT_SUPPORTED]: {
    text: _TL_('Facebook doesn\'t support this location.'),
    shouldDisplayLink: true,
  },
  [LocationTargetErrorType.GOOGLE_NOT_SUPPORTED]: {
    text: _TL_('Google doesn\'t support this location.'),
    shouldDisplayLink: true,
  },
  [LocationTargetErrorType.AUDIENCE_REQUIRED_MIN_RADIUS]: {
    text: _TL_('The minimum radius requirement for special ad category is 15 miles or 25km.'),
    shouldDisplayLink: false,
  },
  [LocationTargetErrorType.AUDIENCE_REQUIRED_POSTAL_CODE_NOT_SUPPORTED]: {
    text: _TL_('ZIP/postal code locations are not supported.'),
    shouldDisplayLink: false,
  },
  [LocationTargetErrorType.FACEBOOK_CALL_EXTENSION_COUNTRY_LIMITED_TARGET]: {
    text: _TL_('Due to a Meta Ads requirement, you\'re only allowed to target countries that match the phone number of your selected business location.'),
    shouldDisplayLink: false,
  },
};

/**
 * Shows the error message underneath each location target in the grid.
 * @param {Object} props - React props.
 * @returns {ReactNode} - React component.
 */
export function LocationTargetErrorMessage(props) {
  const {
    error,
    locationTarget,
    onErrorCalloutShow,
    supportedCountries,
  } = props;
  const i18n = useContext(I18nContext);

  const errorMessage = useMemo(() => errorMessageMap[error], [error]);

  const handleLinkClick = useCallback((event) => {
    onErrorCalloutShow(event, locationTarget, error);
  }, [error, locationTarget, onErrorCalloutShow]);

  if (!errorMessage) {
    return null;
  }

  return (
    <div className="location-target-error-message">
      {i18n.getString(errorMessage.text, { supportedCountries })}
      {' '}
      {errorMessage.shouldDisplayLink && (
        <Link
          as="a"
          className="callout-link"
          onClick={handleLinkClick}
        >
          {i18n.getString(_TL_('How can I fix this?'))}
        </Link>
      )}
    </div>
  );
}

LocationTargetErrorMessage.propTypes = {
  error: PropTypes.oneOf(_.values(LocationTargetErrorType)).isRequired,
  locationTarget: PropTypes.shape({}).isRequired,
  onErrorCalloutShow: PropTypes.func.isRequired,
  supportedCountries: PropTypes.string,
};

LocationTargetErrorMessage.defaultProps = {
  supportedCountries: 'United States',
};
